import { FC } from 'react';
import { Button } from '@/components/ui/button';
import { ShoppingBag, CreditCard, Banknote, Smartphone, Trash2, Bike } from 'lucide-react';
import { CartItem } from '../CartItem';
import { ShoppingCartProps } from './ShoppingCart.types';
import { TaxCalculator } from '@/config/tax';

export const ShoppingCart: FC<ShoppingCartProps> = ({
  items,
  totals,
  deliveryMode = false,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout
}) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const isEmpty = items.length === 0;

  const handleCashPayment = () => {
    onCheckout('efectivo');
  };

  const handleCardPayment = () => {
    onCheckout('tarjeta');
  };

  const handleYapePayment = () => {
    onCheckout('yape');
  };

  const handleDeliveryPayment = () => {
    onCheckout('aplicativo');
  };

  return (
    <div className="h-full flex flex-col bg-green-50 border-l">

      {/* Cart Items - Con altura máxima y scroll interno */}
      <div className="flex-1 overflow-y-auto">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <div className="text-6xl mb-4">🛒</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Tu carrito está vacío
            </h3>
            <p className="text-gray-600">
              Agrega productos para continuar
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {items.map(item => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={onUpdateQuantity}
                onRemove={onRemoveItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Payment Buttons y Total - En la parte inferior */}
      {!isEmpty && (
        <div className="p-4 border-t bg-white">
          <div className="flex justify-between font-bold text-2xl mb-4">
            <span>Total:</span>
            <span className="text-green-600">S/ {totals.total.toFixed(2)}</span>
          </div>
          {deliveryMode ? (
            <Button
              onClick={handleDeliveryPayment}
              disabled={isEmpty}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 text-white h-16 text-lg font-bold shadow-lg rounded-xl transition-all duration-200 active:scale-95 touch-manipulation flex items-center justify-center gap-2"
            >
              <Bike className="h-6 w-6" />
              Pagar Delivery
            </Button>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={handleCashPayment}
                disabled={isEmpty}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500 text-white h-16 text-sm font-bold shadow-lg rounded-xl transition-all duration-200 active:scale-95 touch-manipulation flex-col px-3"
              >
                <Banknote className="h-5 w-5 mb-1" />
                Efectivo
              </Button>
              <Button
                onClick={handleCardPayment}
                disabled={isEmpty}
                variant="outline"
                className="h-16 text-sm font-bold border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-100 disabled:border-gray-300 disabled:text-gray-400 disabled:bg-gray-50 text-blue-700 rounded-xl shadow-md transition-all duration-200 active:scale-95 touch-manipulation flex-col px-3"
              >
                <CreditCard className="h-5 w-5 mb-1" />
                Tarjeta
              </Button>
              <Button
                onClick={handleYapePayment}
                disabled={isEmpty}
                variant="outline"
                className="h-16 text-sm font-bold bg-purple-100 hover:bg-purple-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-300 text-purple-700 border-2 border-purple-300 hover:border-purple-400 rounded-xl shadow-md transition-all duration-200 active:scale-95 touch-manipulation flex-col px-3"
              >
                <Smartphone className="h-5 w-5 mb-1" />
                Yape/Plin
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};